import React, { ReactElement } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../messages/en-US";

interface AssetTemplateDialogFooterProps {
  step: number;
  hasGreasePumps: boolean;
  formValid: boolean;
  isSubmitting?: boolean;
  isBulkUpdating?: boolean;
  bulkUpdateResult?: { totalAssetCount: number; updatedAssetCount: number; results: unknown[] } | null;
  canProceedFromConfiguration: boolean;
  canSaveFromGreasePump: boolean;
  showSourceChoiceInDetails: boolean;
  sourceOption: "new" | "existing";
  baseTemplate: any;
  loadingCopyTemplate: boolean;
  isEdit: boolean;
  isAdministrationMode?: boolean;
  isLoadingImage: boolean;
  onClose: () => void;
  /** Called when Close is clicked after bulk update (refreshes data before closing) */
  onCloseAfterBulkUpdate?: () => void;
  onPrevious: (e?: React.MouseEvent) => void;
  onNext: () => void;
  onSave: () => void;
}

export default function AssetTemplateDialogFooter(
  props: AssetTemplateDialogFooterProps
): ReactElement {
  const {
    step,
    hasGreasePumps,
    formValid,
    isSubmitting = false,
    bulkUpdateResult = null,
    canProceedFromConfiguration,
    canSaveFromGreasePump,
    showSourceChoiceInDetails,
    sourceOption,
    baseTemplate,
    loadingCopyTemplate,
    isEdit,
    isAdministrationMode = false,
    isLoadingImage,
    onClose,
    onCloseAfterBulkUpdate,
    onPrevious,
    onNext,
    onSave,
  } = props;
  const localizationService = useLocalization();

  const isStep0NextDisabled =
    !formValid ||
    (isEdit && isLoadingImage) ||
    (showSourceChoiceInDetails && sourceOption === "existing" && !baseTemplate) ||
    loadingCopyTemplate;

  const isStep1NextDisabled = !formValid || !canProceedFromConfiguration;
  const isStep1SaveDisabled = !formValid || !canProceedFromConfiguration;
  const isStep2SaveDisabled = !formValid || !canSaveFromGreasePump;
  const isUpdateStep = isEdit && !isAdministrationMode && step === (hasGreasePumps ? 3 : 2);
  const showBulkUpdateResult = isUpdateStep && bulkUpdateResult;
  const showNextOnConfig = hasGreasePumps;
  const showNextOnGreasePump = false;
  /** In Administration mode, edit has no Update Assets step; Save on last step saves directly. */
  const shouldSaveDirectlyOnLastStep =
    isAdministrationMode &&
    isEdit &&
    ((step === 1 && !hasGreasePumps) || (step === 2 && hasGreasePumps));

  return (
    <div className="flex justify-end gap-2 mt-auto pt-4 border-t">
      {!isUpdateStep && (
        <Button type="button" themeColor="base" onClick={onClose}>
          {localizationService.toLanguageString("custom.cancel", enMessages.custom.cancel)}
        </Button>
      )}
      {step > 0 && !isUpdateStep && (
        <Button type="button" themeColor="base" onClick={onPrevious}>
          {localizationService.toLanguageString("custom.previous", enMessages.custom.previous)}
        </Button>
      )}
      {step === 0 && (
        <Button
          type="button"
          themeColor="primary"
          disabled={isStep0NextDisabled}
          onClick={(e) => {
            e.preventDefault();
            if (!isStep0NextDisabled) onNext();
          }}
        >
          {localizationService.toLanguageString("custom.next", enMessages.custom.next)}
        </Button>
      )}
      {step === 1 && showNextOnConfig && (
        <Button
          type="button"
          themeColor="primary"
          disabled={isStep1NextDisabled || isSubmitting}
          onClick={(e) => {
            e.preventDefault();
            if (formValid && canProceedFromConfiguration && !isSubmitting) onNext();
          }}
        >
          {localizationService.toLanguageString("custom.next", enMessages.custom.next)}
        </Button>
      )}
      {step === 1 && !showNextOnConfig && (
        <Button
          type="button"
          themeColor="primary"
          disabled={isStep1SaveDisabled || isSubmitting}
          onClick={(e) => {
            e.preventDefault();
            if (formValid && canProceedFromConfiguration && !isSubmitting) {
              shouldSaveDirectlyOnLastStep ? onSave() : isEdit ? onNext() : onSave();
            }
          }}
        >
          {localizationService.toLanguageString("custom.save", enMessages.custom.save)}
        </Button>
      )}
      {step === 2 && showNextOnGreasePump && (
        <Button
          type="button"
          themeColor="primary"
          disabled={isStep2SaveDisabled || isSubmitting}
          onClick={(e) => {
            e.preventDefault();
            if (formValid && canSaveFromGreasePump && !isSubmitting) onNext();
          }}
        >
          {localizationService.toLanguageString("custom.next", enMessages.custom.next)}
        </Button>
      )}
      {step === 2 && !showNextOnGreasePump && !isUpdateStep && (
        <Button
          type="button"
          themeColor="primary"
          disabled={isStep2SaveDisabled || isSubmitting}
          onClick={(e) => {
            e.preventDefault();
            if (formValid && canSaveFromGreasePump && !isSubmitting) {
              shouldSaveDirectlyOnLastStep ? onSave() : isEdit ? onNext() : onSave();
            }
          }}
        >
          {localizationService.toLanguageString("custom.save", enMessages.custom.save)}
        </Button>
      )}
      {isUpdateStep && (
        <Button
          type="button"
          themeColor="primary"
          onClick={(e) => {
            e.preventDefault();
            if (showBulkUpdateResult) {
              onCloseAfterBulkUpdate?.();
            }
            onClose();
          }}
        >
          {localizationService.toLanguageString("custom.close", enMessages.custom.close)}
        </Button>
      )}
    </div>
  );
}
